import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Notifications,
  Security,
  Delete,
  Edit,
  Save,
  Warning,
  CreditCard,
  Add,
  Description,
  Download,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { User } from '../types';
import PaymentMethodSetup from '../components/PaymentMethodSetup';
import ConnectedAgentsCard from '../components/settings/ConnectedAgentsCard';

interface SavedPaymentMethodSummary {
  _id: string;
  cardBrand?: string;
  cardLast4?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  status?: string;
  createdAt?: string;
  lastUsedAt?: string;
  linkedService?: {
    serviceType: string;
    address?: string;
    status?: string;
  };
}

const SettingsPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    // Notification settings
    emailNotifications: true,
    smsNotifications: true,
    bidUpdates: true,
    serviceReminders: true,
    paymentNotifications: false,
    marketingEmails: true,
    newServiceRequestNotifications: true,
    bidAcceptedNotifications: true,
    
    // Security settings
    loginAlerts: true,
  });

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [savedMethods, setSavedMethods] = useState<SavedPaymentMethodSummary[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [removingMethodId, setRemovingMethodId] = useState<string | null>(null);
  const [taxDocumentsLoading, setTaxDocumentsLoading] = useState(false);
  const [taxDocumentsError, setTaxDocumentsError] = useState('');
  const [available1099Forms, setAvailable1099Forms] = useState<Array<{ year: number; filename: string; filepath: string; exists: boolean }>>([]);
  const [w9Info, setW9Info] = useState<{
    w9OnFile: boolean;
    w9SubmittedDate: string | null;
    w9DocumentUrl: string | null;
    w9BusinessName: string | null;
    w9BusinessType: string | null;
  } | null>(null);
  const [pesticideLicenseInfo, setPesticideLicenseInfo] = useState<{
    pesticideLicenseOnFile: boolean;
    pesticideLicenseDocumentUrl: string | null;
    pesticideLicenseNumber: string | null;
    pesticideLicenseExpiryDate: string | null;
    pesticideLicenseSubmittedDate: string | null;
  } | null>(null);
  const isCustomer = user?.userType === 'customer';
  const isProvider = user?.userType === 'provider';
  const navigate = useNavigate();

  useEffect(() => {
    // Load user settings from profile or set defaults
    if (user) {
      const userSettings = (user as any).settings || {};
      setSettings(prev => ({
        ...prev,
        // Load saved settings from user.settings (nested object)
        emailNotifications: userSettings.emailNotifications ?? true,
        smsNotifications: userSettings.smsNotifications ?? true,
        bidUpdates: userSettings.bidUpdates ?? true,
        serviceReminders: userSettings.serviceReminders ?? true,
        paymentNotifications: userSettings.paymentNotifications ?? true, // Default to true per User model
        marketingEmails: userSettings.marketingEmails ?? false,
        newServiceRequestNotifications: userSettings.newServiceRequestNotifications ?? true,
        bidAcceptedNotifications: userSettings.bidAcceptedNotifications ?? true,
        loginAlerts: userSettings.loginAlerts ?? true,
      }));
    }
  }, [user]);

  const fetchSavedMethods = useCallback(async () => {
    if (!isCustomer) {
      return;
    }

    setBillingLoading(true);
    setBillingError('');

    try {
      const response = await apiService.get('/payments/saved-methods');
      if (response.success) {
        setSavedMethods(Array.isArray(response.data) ? response.data : []);
      } else {
        setBillingError(response.error || 'Failed to load payment methods');
        setSavedMethods([]);
      }
    } catch (err: any) {
      setBillingError(err.response?.data?.error || 'Failed to load payment methods');
      setSavedMethods([]);
    } finally {
      setBillingLoading(false);
    }
  }, [isCustomer]);

  useEffect(() => {
    fetchSavedMethods();
  }, [fetchSavedMethods]);

  const fetchTaxDocuments = useCallback(async () => {
    if (!isProvider) {
      return;
    }

    setTaxDocumentsLoading(true);
    setTaxDocumentsError('');

    try {
      // Fetch 1099 forms
      const formsResponse = await apiService.get('/compliance/my-1099-forms');
      if (formsResponse.success) {
        setAvailable1099Forms(Array.isArray(formsResponse.data) ? formsResponse.data : []);
      }

      // Fetch W-9 info
      const w9Response = await apiService.get('/compliance/my-w9');
      if (w9Response.success) {
        setW9Info(w9Response.data as {
          w9OnFile: boolean;
          w9SubmittedDate: string | null;
          w9DocumentUrl: string | null;
          w9BusinessName: string | null;
          w9BusinessType: string | null;
        });
      }

      // Fetch pesticide license info (from user profile)
      const provider = user as any;
      if (provider) {
        setPesticideLicenseInfo({
          pesticideLicenseOnFile: provider.pesticideLicenseOnFile || false,
          pesticideLicenseDocumentUrl: provider.pesticideLicenseDocumentUrl || null,
          pesticideLicenseNumber: provider.pesticideLicenseNumber || null,
          pesticideLicenseExpiryDate: provider.pesticideLicenseExpiryDate || null,
          pesticideLicenseSubmittedDate: provider.pesticideLicenseSubmittedDate || null,
        });
      }
    } catch (err: any) {
      setTaxDocumentsError(err.response?.data?.error || 'Failed to load tax documents');
    } finally {
      setTaxDocumentsLoading(false);
    }
  }, [isProvider]);

  useEffect(() => {
    fetchTaxDocuments();
  }, [fetchTaxDocuments]);

  const handleSettingChange = (setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Send settings nested under 'settings' key to match User model schema
      const response = await apiService.put('/auth/profile', {
        settings: {
          ...settings,
        },
      });

      if (response.success) {
        setSuccess('Settings saved successfully!');
        updateUser(response.data as Partial<User>);
      } else {
        setError(response.error || 'Failed to save settings');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.new !== newPassword.confirm) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.new.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.put('/auth/change-password', {
        currentPassword: newPassword.current,
        newPassword: newPassword.new,
      });

      if (response.success) {
        setSuccess('Password changed successfully!');
        setPasswordDialog(false);
        setNewPassword({ current: '', new: '', confirm: '' });
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const isCustomer = user?.userType === 'customer';
    const requiresPassword = isProvider || isCustomer;
    if (requiresPassword && !deletePassword) {
      setError('Password is required to cancel your account');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = requiresPassword
        ? await apiService.delete('/auth/account', { data: { password: deletePassword } })
        : await apiService.delete('/auth/account');
      
      if (response.success) {
        // Account cancelled, redirect to home
        window.location.href = '/';
      } else {
        setError(response.error || 'Failed to cancel account');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel account');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePaymentMethod = async (methodId: string) => {
    const method = savedMethods.find(m => m._id === methodId);
    if (method?.linkedService) {
      setBillingError(`Cannot remove this payment method. It is currently being used by: ${method.linkedService.serviceType}${method.linkedService.address ? ` at ${method.linkedService.address}` : ''}. Please add another payment method for that service first.`);
      return;
    }

    if (!window.confirm('Remove this payment method? Please add a new payment method first to ensure uninterrupted service.')) {
      return;
    }

    setRemovingMethodId(methodId);
    setBillingError('');

    try {
      const response = await apiService.delete(`/payments/saved-methods/${methodId}`);
      if (response.success) {
        setSuccess('Payment method removed successfully.');
        await fetchSavedMethods();
      } else {
        // SIMPLIFIED: Error message from backend explains why deletion failed
        setBillingError(response.error || 'Failed to remove payment method');
      }
    } catch (err: any) {
      // SIMPLIFIED: Error message from backend explains why deletion failed
      setBillingError(err.response?.data?.error || err.response?.data?.message || 'Failed to remove payment method');
    } finally {
      setRemovingMethodId(null);
    }
  };

  const handlePaymentMethodAdded = async () => {
    setShowAddPaymentDialog(false);
    setSuccess('Payment method saved successfully.');
    await fetchSavedMethods();
  };

  const renderNotificationSettings = () => (
    <Card>
      <CardHeader title="Notification Preferences" avatar={<Notifications />} />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                />
              }
              label="Email Notifications"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smsNotifications}
                  onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                />
              }
              label="SMS Notifications"
            />
          </Grid>
          {user?.userType === 'customer' && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.bidUpdates}
                      onChange={(e) => handleSettingChange('bidUpdates', e.target.checked)}
                    />
                  }
                  label="Bid Updates"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.serviceReminders}
                      onChange={(e) => handleSettingChange('serviceReminders', e.target.checked)}
                    />
                  }
                  label="Service Reminders"
                />
              </Grid>
            </>
          )}
          {user?.userType !== 'admin' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.paymentNotifications}
                    onChange={(e) => handleSettingChange('paymentNotifications', e.target.checked)}
                  />
                }
                label="Payment Notifications"
              />
            </Grid>
          )}
          {user?.userType !== 'admin' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.marketingEmails}
                    onChange={(e) => handleSettingChange('marketingEmails', e.target.checked)}
                  />
                }
                label="Marketing Emails"
              />
            </Grid>
          )}
          {user?.userType === 'provider' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  Provider Notifications
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.newServiceRequestNotifications}
                      onChange={(e) => handleSettingChange('newServiceRequestNotifications', e.target.checked)}
                    />
                  }
                  label="New Service Requests in My Area"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Get notified when customers post service requests in your service areas
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.bidAcceptedNotifications}
                      onChange={(e) => handleSettingChange('bidAcceptedNotifications', e.target.checked)}
                    />
                  }
                  label="Bid Accepted Notifications"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Get notified when customers accept your bids
                </Typography>
              </Grid>
            </>
          )}
          {user?.userType === 'admin' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  Admin Notifications
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.newServiceRequestNotifications}
                      onChange={(e) => handleSettingChange('newServiceRequestNotifications', e.target.checked)}
                    />
                  }
                  label="New Service Requests"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Get notified when new service requests are created on the platform
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.bidAcceptedNotifications}
                      onChange={(e) => handleSettingChange('bidAcceptedNotifications', e.target.checked)}
                    />
                  }
                  label="Provider Activity Notifications"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Get notified about important provider activities and platform events
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );


  const renderBillingSettings = () => {
    if (!isCustomer) {
      return null;
    }

    return (
      <Card>
        <CardHeader
          title="Billing & Payments"
          avatar={<CreditCard />}
          action={
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setShowAddPaymentDialog(true)}
            >
              Add Payment Method
            </Button>
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To ensure uninterrupted service, we require at least one active payment method on file. 
            This payment method will be used for all payments processed through the platform. 
            If you'd like to update your payment method, please add a new card first, then you can remove the previous one.
          </Typography>

          {billingError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillingError('')}>
              {billingError}
            </Alert>
          )}

          {billingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : savedMethods.length === 0 ? (
            <Box
              sx={{
                p: 2,
                border: '1px dashed',
                borderColor: 'grey.300',
                borderRadius: 1,
                textAlign: 'center',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="subtitle2">No payment method on file</Typography>
              <Typography variant="body2" color="text.secondary">
                Add a card to keep upcoming services on the schedule.
              </Typography>
            </Box>
          ) : (
            <List>
              {savedMethods.map((method) => {
                const label = `${(method.cardBrand || 'Card').toUpperCase()} •••• ${method.cardLast4 || '0000'}`;
                const expiry =
                  method.cardExpMonth && method.cardExpYear
                    ? `${String(method.cardExpMonth).padStart(2, '0')}/${String(method.cardExpYear).slice(-2)}`
                    : 'Unknown expiry';

                return (
                  <ListItem
                    key={method._id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      mb: 1,
                    }}
                    secondaryAction={
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRemovePaymentMethod(method._id)}
                        disabled={removingMethodId === method._id}
                      >
                        {removingMethodId === method._id ? <CircularProgress size={18} /> : 'Remove'}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={label}
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Expires {expiry}
                            </Typography>
                            <Chip size="small" label={method.status || 'active'} />
                            {method.lastUsedAt && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Last used ${new Date(method.lastUsedAt).toLocaleDateString()}`}
                              />
                            )}
                          </Box>
                          {method.linkedService && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={`Used for: ${method.linkedService.serviceType}${method.linkedService.address ? ` at ${method.linkedService.address}` : ''} (${method.linkedService.status || 'active'})`}
                              />
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCustomerAgreements = () => {
    if (!isProvider) return null;
    return (
      <Card>
        <CardHeader title="Customer Agreements" avatar={<Description />} />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload your own service agreement for each service. Customers e-sign it when they accept your bid or
            onboarding offer, and you can download every signed copy.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/provider/customer-agreements')}>
            Manage Customer Agreements
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderTaxDocuments = () => {
    // Debug: Log provider status
    console.log('[TAX-DOCUMENTS] Rendering tax documents section', { isProvider, userType: user?.userType });
    
    if (!isProvider) {
      return null;
    }

    const handleDownload1099 = (filepath: string, filename: string) => {
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = filepath;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <Card>
        <CardHeader
          title="Tax Documents"
          avatar={<Description />}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Access your W-9 form status and download your 1099-NEC tax forms for previous years.
          </Typography>

          {taxDocumentsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTaxDocumentsError('')}>
              {taxDocumentsError}
            </Alert>
          )}

          {/* W-9 Status */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              W-9 Form Status
            </Typography>
            {taxDocumentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : w9Info ? (
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: w9Info.w9OnFile ? 'success.main' : 'warning.main',
                  borderRadius: 1,
                  bgcolor: w9Info.w9OnFile ? 'success.50' : 'warning.50',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {w9Info.w9OnFile ? (
                    <CheckCircle color="success" />
                  ) : (
                    <ErrorIcon color="warning" />
                  )}
                  <Typography variant="body2" fontWeight="bold">
                    {w9Info.w9OnFile ? 'W-9 Form On File' : 'W-9 Form Not Submitted'}
                  </Typography>
                </Box>
                {w9Info.w9OnFile && w9Info.w9SubmittedDate && (
                  <Typography variant="body2" color="text.secondary">
                    Submitted: {new Date(w9Info.w9SubmittedDate).toLocaleDateString()}
                  </Typography>
                )}
                {w9Info.w9BusinessName && (
                  <Typography variant="body2" color="text.secondary">
                    Business Name: {w9Info.w9BusinessName}
                  </Typography>
                )}
                {w9Info.w9OnFile && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const base = process.env.REACT_APP_API_URL
                            ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                            : window.location.origin + '/api';
                          const r = await fetch(`${base}/compliance/my-w9-document`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (!r.ok) {
                            const err = await r.json().catch(() => ({}));
                            throw new Error(err.error || 'Download failed');
                          }
                          const blob = await r.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'W-9-on-file.pdf';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (e: any) {
                          setTaxDocumentsError(e.message || 'Failed to download W-9');
                        }
                      }}
                    >
                      Download W-9 on file
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const base = process.env.REACT_APP_API_URL
                            ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                            : window.location.origin + '/api';
                          const r = await fetch(`${base}/compliance/my-w9-summary`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          if (!r.ok) {
                            const err = await r.json().catch(() => ({}));
                            throw new Error(err.error || 'Download failed');
                          }
                          const blob = await r.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'W-9-information-on-file.pdf';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (e: any) {
                          setTaxDocumentsError(e.message || 'Failed to download summary');
                        }
                      }}
                    >
                      Preview W-9 data (summary)
                    </Button>
                  </Box>
                )}
                {!w9Info.w9OnFile && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => window.location.href = '/provider/w9-upload'}
                  >
                    Upload W-9 Form
                  </Button>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Unable to load W-9 information
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Pesticide License Status */}
          {(user as any)?.services?.includes('weedControlFertilizing') && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Pesticide License Status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                IDOA Commercial Pesticide Applicator License is required to bid on marketplace Weed Control/Fertilizing requests. You remain responsible for complying with pesticide laws for your own customers.
              </Typography>
              {pesticideLicenseInfo ? (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: pesticideLicenseInfo.pesticideLicenseOnFile ? 'success.main' : 'warning.main',
                    borderRadius: 1,
                    bgcolor: pesticideLicenseInfo.pesticideLicenseOnFile ? 'success.50' : 'warning.50',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {pesticideLicenseInfo.pesticideLicenseOnFile ? (
                      <CheckCircle color="success" />
                    ) : (
                      <ErrorIcon color="warning" />
                    )}
                    <Typography variant="body2" fontWeight="bold">
                      {pesticideLicenseInfo.pesticideLicenseOnFile ? 'Pesticide License On File' : 'Pesticide License Not Submitted'}
                    </Typography>
                  </Box>
                  {pesticideLicenseInfo.pesticideLicenseOnFile && pesticideLicenseInfo.pesticideLicenseSubmittedDate && (
                    <Typography variant="body2" color="text.secondary">
                      Submitted: {new Date(pesticideLicenseInfo.pesticideLicenseSubmittedDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {pesticideLicenseInfo.pesticideLicenseNumber && (
                    <Typography variant="body2" color="text.secondary">
                      License Number: {pesticideLicenseInfo.pesticideLicenseNumber}
                    </Typography>
                  )}
                  {pesticideLicenseInfo.pesticideLicenseExpiryDate && (
                    <Typography variant="body2" color="text.secondary">
                      Expires: {new Date(pesticideLicenseInfo.pesticideLicenseExpiryDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {!pesticideLicenseInfo.pesticideLicenseOnFile && (
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => window.location.href = '/provider/pesticide-license-upload'}
                    >
                      Upload Pesticide License
                    </Button>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Unable to load pesticide license information
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* 1099 Forms */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Form 1099-NEC Downloads
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download your 1099-NEC forms for tax filing. Forms are available for years where you earned $600 or more.
            </Typography>

            {taxDocumentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : available1099Forms.length === 0 ? (
              <Box
                sx={{
                  p: 2,
                  border: '1px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No 1099 forms available yet. Forms will appear here after they are generated for tax years where you earned $600 or more.
                </Typography>
              </Box>
            ) : (
              <List>
                {available1099Forms.map((form) => (
                  <ListItem
                    key={form.year}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: 1,
                      mb: 1,
                    }}
                    secondaryAction={
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={() => handleDownload1099(form.filepath, form.filename)}
                      >
                        Download
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={`Form 1099-NEC for ${form.year}`}
                      secondary={`Tax year ${form.year} - Nonemployee Compensation`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSecuritySettings = () => (
    <Card>
      <CardHeader title="Security Settings" avatar={<Security />} />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2">Password</Typography>
                <Typography variant="body2" color="text.secondary">
                  Last changed: {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'Unknown'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setPasswordDialog(true)}
              >
                Change Password
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.loginAlerts}
                  onChange={(e) => handleSettingChange('loginAlerts', e.target.checked)}
                />
              }
              label="Login Alerts"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Get notified when someone logs into your account
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAccountSettings = () => (
    <Card>
      <CardHeader title="Account Settings" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Data Retention Policy
            </Typography>
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body2" color="text.secondary">
                For your privacy and security, your account data will be automatically deleted after 1 year of inactivity (no logins, no services, no payments). This includes:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Profile information and contact details
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Service request history
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Payment records (anonymized for accounting)
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  Messages and notifications
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" color="error">
                  Cancel Account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cancel your account. Account information and financial data will be retained for legal and tax purposes.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteDialog(true)}
              >
                Cancel Account
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Please log in to access settings.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {user?.userType === 'admin' 
          ? 'Manage your admin account preferences and notification settings'
          : 'Manage your account preferences and privacy settings'
        }
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {renderNotificationSettings()}
        {renderBillingSettings()}
        {renderCustomerAgreements()}
        {isProvider && <ConnectedAgentsCard />}
        {renderTaxDocuments()}
        {renderSecuritySettings()}
        {renderAccountSettings()}
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSaveSettings}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Save Settings'}
        </Button>
      </Box>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={newPassword.current}
            onChange={(e) => setNewPassword(prev => ({ ...prev, current: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={newPassword.new}
            onChange={(e) => setNewPassword(prev => ({ ...prev, new: e.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            value={newPassword.confirm}
            onChange={(e) => setNewPassword(prev => ({ ...prev, confirm: e.target.value }))}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Account Dialog */}
      <Dialog open={deleteDialog} onClose={() => {
        setDeleteDialog(false);
        setDeletePassword('');
        setError('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
          Cancel Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your account? This will:
          </DialogContentText>
          <List dense>
            <ListItem>
              <ListItemText primary="• Disable your account and revoke platform access" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Cancel all active service requests and bids" />
            </ListItem>
            {isProvider && (
              <ListItem>
                <ListItemText primary="• Cancel all active customer relationships and service schedules" />
              </ListItem>
            )}
            <ListItem>
              <ListItemText primary="• Preserve all financial records, payment history, and tax information for legal compliance" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Retain account information and service history for legal and tax purposes" />
            </ListItem>
          </List>
          {user?.userType === 'customer' && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
              If you only wish to cancel one or more services and keep your account, you may request cancellation for each service from <strong>My Services</strong> on your dashboard. You do not need to cancel your account to stop individual services.
            </DialogContentText>
          )}
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Note: Your account information and financial data will be retained as required by law for tax and legal compliance purposes. This includes payment history, service records, and all transaction data.
          </DialogContentText>
          {(isProvider || user?.userType === 'customer') && (
            <>
              <DialogContentText sx={{ mt: 2, color: 'error.main', fontWeight: 'bold' }}>
                To confirm account cancellation, please enter your password:
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                label="Password"
                type="password"
                fullWidth
                variant="outlined"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                error={!!error && !deletePassword}
                helperText={error && !deletePassword ? error : ''}
                sx={{ mt: 2 }}
              />
            </>
          )}
          {error && deletePassword && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialog(false);
            setDeletePassword('');
            setError('');
          }}>Cancel</Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained" 
            disabled={loading || ((isProvider || user?.userType === 'customer') && !deletePassword)}
          >
            {loading ? <CircularProgress size={20} /> : 'Cancel Account'}
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentMethodSetup
        open={showAddPaymentDialog}
        onClose={() => setShowAddPaymentDialog(false)}
        onSuccess={handlePaymentMethodAdded}
      />
    </Container>
  );
};

export default SettingsPage;